import React, { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { adminApi, extractError } from '../api/client';
import { SECTION_CONFIGS, SECTION_ORDER } from '../lib/countySections';

/**
 * Shared layout for any county-scoped civic data page.
 * Loads the state + county + summary counts, renders a breadcrumb and a
 * horizontal section pill-nav, then renders children.
 */
export default function CountyWorkspace({ stateId, countyId, activeSection, title, breadcrumbTrail = [], children, actions }) {
  const [state, setState] = useState(null);
  const [county, setCounty] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [s, c, sum] = await Promise.all([
          adminApi.get(`/admin/states/${stateId}`),
          adminApi.get(`/admin/states/${stateId}/counties/${countyId}`),
          adminApi.get(`/admin/states/${stateId}/counties/${countyId}/summary`),
        ]);
        if (!active) return;
        setState(s.data);
        setCounty(c.data);
        setSummary(sum.data);
      } catch (e) {
        if (active) setError(extractError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [stateId, countyId]);

  if (loading) return <div className="text-center p-5"><Spinner animation="border" /></div>;
  if (error) return <div className="page-content"><div className="alert alert-danger">{error}</div></div>;

  return (
    <>
      <div className="page-header">
        <div style={{ minWidth: 0 }}>
          <div className="text-muted" style={{ fontSize: '0.82rem', marginBottom: 4 }}>
            <Link to="/admin/states" className="text-decoration-none">States</Link>
            {' / '}
            <Link to={`/admin/states/${stateId}`} className="text-decoration-none">{state?.name}</Link>
            {' / '}
            <Link to={`/admin/states/${stateId}/counties`} className="text-decoration-none">Counties</Link>
            {' / '}
            <Link to={`/admin/states/${stateId}/counties/${countyId}`} className="text-decoration-none">{county?.name}</Link>
            {breadcrumbTrail.map((b, i) => (
              <React.Fragment key={i}>
                {' / '}
                {b.to ? <Link to={b.to} className="text-decoration-none">{b.label}</Link> : b.label}
              </React.Fragment>
            ))}
          </div>
          <h1 data-testid="page-title">{title || `${county?.name} County`}</h1>
          <div className="subtitle">{state?.name} ({state?.abbreviation}){county?.county_code ? ` · FIPS ${county.county_code}` : ''}</div>
        </div>
        {actions && <div className="d-flex gap-2 align-items-center">{actions}</div>}
      </div>

      <div className="px-4 pt-3 pb-1 d-flex flex-wrap gap-2" style={{ borderBottom: '1px solid var(--border)', background: 'var(--app-surface)' }}>
        {SECTION_ORDER.map((slug) => {
          const cfg = SECTION_CONFIGS[slug];
          const counts = summary?.[
            slug === 'election-dates' ? 'election_dates'
            : slug === 'supervisors' ? 'supervisors'
            : slug === 'party-organizations' ? 'party_organizations'
            : 'local_groups'
          ] || { total: 0, published: 0 };
          return (
            <NavLink
              key={slug}
              to={`/admin/states/${stateId}/counties/${countyId}/${slug}`}
              className={({ isActive }) =>
                `filter-chip ${isActive || activeSection === slug ? 'fw-semibold' : ''}`
              }
              style={({ isActive }) => isActive || activeSection === slug
                ? { borderColor: 'var(--primary)', background: 'var(--primary-soft)', color: 'var(--primary-hover)' }
                : {}}
              data-testid={`county-section-nav-${slug}`}
            >
              <i className={`bi ${cfg.icon}`} aria-hidden="true" />
              <span className="ms-1">{cfg.titlePlural}</span>
              <span className="badge bg-light text-dark border ms-2" style={{ fontSize: '0.7rem' }}>{counts.total}</span>
            </NavLink>
          );
        })}
      </div>

      <div className="page-content">{children}</div>
    </>
  );
}
